import React from 'react';
import PropTypes from 'prop-types';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Rating from '@material-ui/lab/Rating';

import checkMarkIcon from '../../../media/check-mark.svg';
import exclamationMarkIcon from '../../../media/exclamation-mark.svg';
import { Star } from '../star';
import { Spinner } from '../spinner';
import { CheckboxOff, CheckboxOn } from '../checkbox';
import { useStepper, ResponseCodes, StagesList } from '../../hooks/use-stepper';

import {
  OuterWrapper,
  InnerWrapper,
  StepperHeading,
  Step,
  StepLabel,
  StepIcon,
  StepIconImg,
  ControlsWrapper,
  ControlsGroupWrapper,
  TextFieldWrapper,
  TextField,
  SelectWrapper,
  CheckboxWrapper,
  CheckboxLabel,
  InlineLink,
  ControlWrapper,
  StarIconWrapper,
  StarCaption,
  Heading,
  RatingWrapper,
  Text,
  DiscountText,
  Button,
  Link,
  InfoWrapper,
  ErrorMessage,
  ErrorMessageIconImg,
  MenuItemWrap,
} from './styled';

const CustomStepIcon = ({ active, completed }) => (
  <StepIcon completed={completed} active={active}>
    {completed && <StepIconImg src={checkMarkIcon} alt="Check mark" />}
  </StepIcon>
);
CustomStepIcon.propTypes = {
  active: PropTypes.bool.isRequired,
  completed: PropTypes.bool.isRequired,
};

const Submit = ({ text, disabled, loading }) => (
  <ControlWrapper>
    {loading ? (
      <Spinner />
    ) : (
      <Button
        disabled={disabled}
        type="submit"
        variant="contained"
        disableElevation
        fullWidth
      >
        {text}
      </Button>
    )}
  </ControlWrapper>
);
Submit.propTypes = {
  text: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
};
Submit.defaultProps = {
  disabled: false,
  loading: false,
};

const AlreadySubmittedError = () => (
  <ErrorMessage>
    <ErrorMessageIconImg src={exclamationMarkIcon} alt="Exclamation mark" />
    <span>
      You have already submitted feedback and recieved your promo code. Please
      check your email
      <br />
      or contact us at
      <InlineLink href="mailto:hi@evapolar.com">
        {` hi@evapolar.com `}
      </InlineLink>
      if you have any questions.
    </span>
  </ErrorMessage>
);

const UndefinedError = () => (
  <ErrorMessage>
    <span>
      Something went wrong, please try again or contact us at
      <InlineLink href="mailto:hi@evapolar.com">
        {` hi@evapolar.com`}
      </InlineLink>
    </span>
  </ErrorMessage>
);

const getStepByStage = (stage) => {
  if (stage === StagesList.ThankYou) {
    return 3;
  }
  if ([StagesList.Discount, StagesList.GettingDiscount].includes(stage)) {
    return 2;
  }
  if ([StagesList.Stars, StagesList.SubmittingStars].includes(stage)) {
    return 1;
  }

  return 0;
};

export const Stepper = () => {
  const {
    sourcesList,
    stepsList,
    starsList,
    stage,
    responseCodes,
    customerName,
    email,
    source,
    checked,
    stars,
    handleCustomerNameChange,
    handleEmailChange,
    handleSourceChange,
    toggleChecked,
    toStarsStage,
    handleStars,
    submitStars,
    getTheDiscount,
  } = useStepper();

  return (
    <OuterWrapper>
      <InnerWrapper>
        <StepperHeading activeStep={getStepByStage(stage)} connector={null}>
          {stepsList.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
            </Step>
          ))}
        </StepperHeading>
        {stage === StagesList.Initial && (
          <form onSubmit={toStarsStage}>
            <ControlsWrapper>
              <ControlsGroupWrapper>
                <TextFieldWrapper>
                  <TextField
                    required
                    fullWidth
                    value={customerName}
                    onChange={handleCustomerNameChange}
                    placeholder="Name"
                    variant="outlined"
                  />
                </TextFieldWrapper>
                <TextFieldWrapper>
                  <TextField
                    required
                    fullWidth
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Email address"
                    variant="outlined"
                  />
                </TextFieldWrapper>
              </ControlsGroupWrapper>
              <SelectWrapper>
                <TextField
                  required
                  fullWidth
                  select
                  value={source}
                  onChange={handleSourceChange}
                  label={source ? null : 'Where did you buy the product?'}
                  variant="outlined"
                >
                  {sourcesList.map((sourceItem) => (
                    <MenuItemWrap key={sourceItem} value={sourceItem}>
                      {sourceItem}
                    </MenuItemWrap>
                  ))}
                </TextField>
              </SelectWrapper>
            </ControlsWrapper>
            <CheckboxWrapper>
              <FormControlLabel
                control={
                  <Checkbox
                    iconStyle={{ fill: 'white' }}
                    required
                    icon={<CheckboxOff />}
                    checkedIcon={<CheckboxOn />}
                    checked={checked}
                    onChange={toggleChecked}
                  />
                }
                label={
                  <span>
                    <CheckboxLabel>
                      By clicking Continue, you agree to Evapolar’s{' '}
                    </CheckboxLabel>
                    <InlineLink
                      href="https://evapolar.com/terms/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy and Terms of Service.
                    </InlineLink>
                  </span>
                }
              />
            </CheckboxWrapper>
            <Submit text="Continue" />
          </form>
        )}
        {[StagesList.Stars, StagesList.SubmittingStars].includes(stage) &&
          responseCodes.submitStars === ResponseCodes.AlreadySubmitted && (
            <AlreadySubmittedError />
          )}
        {[StagesList.Stars, StagesList.SubmittingStars].includes(stage) &&
          responseCodes.submitStars === ResponseCodes.UndefinedError && (
            <UndefinedError />
          )}
        {[StagesList.Stars, StagesList.SubmittingStars].includes(stage) &&
          responseCodes.submitStars === null && (
            <form onSubmit={submitStars}>
              <Heading>How would you rate your purchase?</Heading>
              <RatingWrapper>
                <Rating
                  name="rating"
                  value={stars}
                  onChange={handleStars}
                  emptyIcon={<Star />}
                  icon={<Star filled />}
                  IconContainerComponent={({ value, ...other }) => (
                    <>
                      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                      <StarIconWrapper {...other} />
                      <StarCaption bold={stars >= value}>
                        {starsList[value - 1]}
                      </StarCaption>
                    </>
                  )}
                />
              </RatingWrapper>
              <Submit
                text="Continue"
                disabled={stars < 1}
                loading={stage === StagesList.SubmittingStars}
              />
            </form>
          )}
        {[StagesList.Discount, StagesList.GettingDiscount].includes(stage) &&
          responseCodes.getTheDiscount === ResponseCodes.AlreadySubmitted && (
            <AlreadySubmittedError />
          )}
        {[StagesList.Discount, StagesList.GettingDiscount].includes(stage) &&
          responseCodes.getTheDiscount === ResponseCodes.UndefinedError && (
            <UndefinedError />
          )}
        {[StagesList.Discount, StagesList.GettingDiscount].includes(stage) &&
          stars <= 3 && (
            <InfoWrapper>
              <Text>
                We are sorry to hear that something went bad. We would love to
                learn more about your experience. Please use the button below to
                contact our customer support service.
              </Text>
              <ControlWrapper>
                <Link
                  href="https://support.evapolar.com/hc/en-us/requests/new?ticket_form_id=233169"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Tell us more
                </Link>
              </ControlWrapper>
              <Text>
                We try to improve and upgrade our product regularly, and your
                opinion is very important to us.
              </Text>
            </InfoWrapper>
          )}
        {[StagesList.Discount, StagesList.GettingDiscount].includes(stage) &&
          responseCodes.getTheDiscount === null &&
          stars > 3 && (
            <InfoWrapper as="form" onSubmit={getTheDiscount}>
              <Heading>We love to hear you enjoyed your purchase!</Heading>
              {source === 'Amazon' ? (
                <Text>
                  We’d love it if you could take a few minutes to leave a review
                  on Amazon. To thank you, we will offer you a special bonus.
                </Text>
              ) : (
                <Text>
                  We would like to offer you
                  <DiscountText> 15% OFF </DiscountText>
                  for your next purchase on Evapolar website. The discount code
                  will be sent to your email address.
                </Text>
              )}
              <Submit
                text={
                  source === 'Amazon' ? 'Get the bonus' : 'Get the discount'
                }
                loading={stage === StagesList.GettingDiscount}
              />
            </InfoWrapper>
          )}
        {stage === StagesList.ThankYou && (
          <InfoWrapper>
            <Heading>Thank you!</Heading>
            {source === 'Amazon' ? (
              <Text>
                Instructions on how to submit the review and information
                regarding the bonus was sent to your email address.
              </Text>
            ) : (
              <Text>The discount code was sent to your email address.</Text>
            )}
            <ControlWrapper>
              <Link href="https://evapolar.com/">Homepage</Link>
            </ControlWrapper>
            <Text small>
              If you did not receive an email, please check your spam folder, or
              contact us at
              <InlineLink href="mailto:hi@evapolar.com">
                {` hi@evapolar.com`}
              </InlineLink>
            </Text>
          </InfoWrapper>
        )}
      </InnerWrapper>
    </OuterWrapper>
  );
};
